import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import type { CreateFormSubmissionDto, FormAnswerValueDto } from "src/http";
import { FetchError, queryKeys, useAccessToken } from "src/http";

type Variables = {
  formGuid: string;
  createdDateTimeUtc: string;
  recipientUserId: string;
  dateRangeStartUtc: string | null;
  dateRangeEndUtc: string | null;
  videoUrl: string | null;
  answers: {
    questionGuid: string;
    value: FormAnswerValueDto | null;
  }[];
  referencedActionItemGuids: string[];
  referencedTaskItemGuids: string[];
  submitted: boolean;
};

const useCreateFormSubmission = () => {
  const { getAccessToken } = useAccessToken();
  const queryClient = useQueryClient();

  async function createFormSubmissionRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: CreateFormSubmissionDto = {
      createdDateTimeUtc: variables.createdDateTimeUtc,
      recipientUserId: variables.recipientUserId,
      dateRangeStartUtc: variables.dateRangeStartUtc,
      dateRangeEndUtc: variables.dateRangeEndUtc,
      videoUrl: variables.videoUrl,
      answers: variables.answers,
      referencedActionItemGuids: variables.referencedActionItemGuids,
      referencedTaskItemGuids: variables.referencedTaskItemGuids,
      submitted: variables.submitted,
    };

    const response = await fetch(`${configuration.apiRootUrl}/forms/${variables.formGuid}/form-submissions`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return useMutation({
    mutationFn: createFormSubmissionRequest,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.formSubmissions.getFormSubmissions());
    },
  });
};

export { useCreateFormSubmission };
